import type { OrgTeamWithApps } from '../../../api/recruit'
import type { OrgSeason } from '../../../api/teams'
import type { UpcomingEntry } from '../upcoming'

/**
 * Derivations shared by a widget and by the count that decides whether it
 * appears at all.
 *
 * A widget's `relevance` reads counts, and its body reads rows. If those two
 * disagree — the count says three, the body renders none — the board shows an
 * empty panel that claims to be full. Deriving both from one function here is
 * what stops that drifting.
 */

/**
 * How many different reasons the schedule's rows are on the list for.
 *
 * This is what decides whether rows carry a "why" badge and whether the legend
 * explaining those badges appears — NOT how many roles the viewer holds. The
 * two are not the same thing: someone can run a league, own a club and play,
 * and still have every fixture on their list for one single reason.
 *
 * Getting that wrong is what put a three-badge key on a board whose schedule was
 * empty, explaining two badges that nothing can currently produce.
 */
export function distinctReasons(upcoming: UpcomingEntry[]): number {
  return new Set(upcoming.map((entry) => entry.reason)).size
}

/** Seasons with play under way or about to be. Excludes drafts. */
const RUNNING_STATUSES: OrgSeason['status'][] = ['open', 'active', 'playoffs']

export function runningSeasons(seasons: OrgSeason[]): OrgSeason[] {
  return seasons.filter((s) => RUNNING_STATUSES.includes(s.status))
}

export type KeyDateKind =
  | 'registration-opens'
  | 'registration-closes'
  | 'season-starts'
  | 'season-ends'

export interface KeyDate {
  /** Unique per (season, kind) — a season contributes up to four of these. */
  id: string
  seasonID: string
  /** The league that owns it — org-scoped routes are keyed by that org's slug. */
  orgID: string
  seasonName: string
  kind: KeyDateKind
  label: string
  at: Date
}

const KEY_DATE_LABEL: Record<KeyDateKind, string> = {
  'registration-opens': 'Registration opens',
  'registration-closes': 'Registration closes',
  'season-starts': 'Season starts',
  'season-ends': 'Season ends',
}

/** How many a side panel can hold before it stops being a summary. */
export const MAX_KEY_DATES = 5

/**
 * What a league operator is counting down to.
 *
 * Only dates still ahead: a deadline that has passed is not a key date, it is
 * history, and the season table already says what state that left the season
 * in. Drafts count here even though they are filtered out of the season table —
 * a draft season's registration date is precisely the thing worth knowing.
 */
export function keyDates(seasons: OrgSeason[], now: Date): KeyDate[] {
  const out: KeyDate[] = []

  for (const season of seasons) {
    const fields: [KeyDateKind, string | null][] = [
      ['registration-opens', season.registration_opens_at],
      ['registration-closes', season.registration_closes_at],
      ['season-starts', season.starts_at],
      ['season-ends', season.ends_at],
    ]

    for (const [kind, raw] of fields) {
      if (!raw) continue
      const at = new Date(raw)
      // A malformed date from the API must not become an "Invalid Date" row.
      if (Number.isNaN(at.getTime())) continue
      if (at.getTime() <= now.getTime()) continue
      out.push({
        id: `${season.id}:${kind}`,
        seasonID: season.id,
        orgID: season.organization_id,
        seasonName: season.name,
        kind,
        label: KEY_DATE_LABEL[kind],
        at,
      })
    }
  }

  return out.sort((a, b) => a.at.getTime() - b.at.getTime()).slice(0, MAX_KEY_DATES)
}

/** Teams with someone actually waiting on a decision. */
export function pendingRosters(rosters: OrgTeamWithApps[]): OrgTeamWithApps[] {
  return rosters
    .filter((team) => team.pending_count > 0)
    .sort((a, b) => b.pending_count - a.pending_count)
}

/** Total people waiting across every team. */
export function pendingCount(rosters: OrgTeamWithApps[]): number {
  return rosters.reduce((total, team) => total + Math.max(0, team.pending_count), 0)
}
